import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * The slice of the native SunmiPrinter plugin this file uses.
 *
 * Declared here rather than imported from @pubq/sunmi-printer: that package is a file:
 * dependency on the sunmi-printer submodule whose dist/ has never been built, so its main,
 * module and types all point at a directory that does not exist - which is why nothing in
 * src/ has ever imported it. The native half is in the APK either way, because Capacitor
 * picks the android project up from the same dependency, so the plugin answers to its name.
 *
 * Keep these signatures matching sunmi-printer/src/definitions.ts. hasPrinter resolves
 * { result } and printString resolves { results } - the inconsistency is the plugin's.
 */
interface SunmiPrinterPlugin {
  hasPrinter(): Promise<{ result: boolean }>;
  printString(options: { contents: string; qrCode: string }): Promise<{ results?: boolean }>;
}

const SunmiPrinter = registerPlugin<SunmiPrinterPlugin>('SunmiPrinter');

/**
 * The screen's own printer: the built-in printer of a Sunmi device, reached through the APK's
 * SunmiPrinter plugin. Never present in a browser or on a device without one.
 *
 * Everything else the KDS prints goes out over the network, to a printer the Prime API knows by
 * MAC address (PrintService.printByApi / printCompletedOrderReceipt). The built-in printer has
 * no MAC and no server-side layout, so it is identified by this id instead and the layout is
 * built here. The id has to be something no real MAC can collide with.
 */
export const INTERNAL_PRINTER_ID = 'sunmi-internal';

export function isInternalPrinter(printerId: string | null | undefined): boolean {
  return printerId === INTERNAL_PRINTER_ID;
}

/**
 * Whether the built-in printer can be printed on right now.
 *
 * Binding to Sunmi's printer service is asynchronous, so this answers false for a moment after
 * the app starts and true afterwards: ask again rather than caching the first answer. The
 * plugin's own hasPrinter() reports the bind ATTEMPT for the inner printer, not a live
 * connection, so on a non-Sunmi Android device that happened to accept the bind this can be
 * optimistic. It is the strongest signal the plugin offers today; a print that fails anyway
 * surfaces as a print error rather than silently vanishing.
 */
export async function innerPrinterAvailable(): Promise<boolean> {
  if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable('SunmiPrinter')) {
    return false;
  }
  try {
    const { result } = await SunmiPrinter.hasPrinter();
    return !!result;
  } catch {
    return false;
  }
}

/**
 * Print a block of ESC/POS commands on the built-in printer.
 *
 * printString() is the only plugin method that covers every Sunmi family: it tries a paired
 * Bluetooth printer, then the K1's AIDL service, then the inner printer of a T2s / K2 Mini / V2,
 * and the string's bytes reach the printer as written. printText() and sendRawData() go only
 * through the K1 implementation and do nothing on the other devices, which is why they are not
 * used here even though their names fit better.
 *
 * The commands are UTF-8, so å/ä/ö need no code page selection.
 */
export async function printOnInnerPrinter(commands: string): Promise<void> {
  const { results } = await SunmiPrinter.printString({
    contents: commands,
    // The plugin prints a QR code after the text on the K1 path. An empty string is how you
    // ask it not to; a missing key would be read as null and crash the AIDL call.
    qrCode: '',
  });
  // The inner-printer path reports the printer's own verdict; the Bluetooth path resolves with
  // nothing to say. Only an explicit false is a failure.
  if (results === false) {
    throw new Error('INNER_PRINTER_FAILED');
  }
}

/** Characters per line in the normal font: 384 dots on 58 mm paper, 12 dots per character. */
export const INNER_PRINTER_COLUMNS = 32;

const ESC = '\x1B';
const GS = '\x1D';

export const INNER = {
  init: `${ESC}@`,
  newLine: '\n',
  boldOn: `${ESC}E\x01`,
  boldOff: `${ESC}E\x00`,
  alignLeft: `${ESC}a\x00`,
  alignCenter: `${ESC}a\x01`,
  doubleSize: `${GS}!\x11`,
  normalSize: `${GS}!\x00`,
  reverseOn: `${GS}B\x01`,
  reverseOff: `${GS}B\x00`,
} as const;

/**
 * Feed enough paper for the tear bar to clear the last line.
 *
 * The built-in printer has no cutter on most of these devices, so the ticket is torn off by
 * hand. Without the feed the last line sits inside the mechanism and has to be pulled out.
 */
export const INNER_TEAR_OFF = '\n\n\n';

/** Two columns on one line: label left, value right, clipped rather than wrapped. */
export function innerColumns(left: string, right: string, width = INNER_PRINTER_COLUMNS): string {
  const value = String(right ?? '');
  const label = String(left ?? '');
  const room = Math.max(0, width - value.length - 1);
  const clipped = label.length > room ? label.slice(0, room) : label;
  const gap = Math.max(1, width - clipped.length - value.length);
  return `${clipped}${' '.repeat(gap)}${value}`;
}

/** Wrap on spaces, so a long item name does not lose its tail to the paper edge. */
export function innerWrap(text: string, width = INNER_PRINTER_COLUMNS, indent = ''): string[] {
  const words = String(text ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return [];
  const room = Math.max(1, width - indent.length);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    if (!line) {
      line = word.length > room ? word.slice(0, room) : word;
      // A single word longer than the line: keep the remainder rather than dropping it.
      let rest = word.length > room ? word.slice(room) : '';
      while (rest) {
        lines.push(indent + line);
        line = rest.slice(0, room);
        rest = rest.slice(room);
      }
      continue;
    }
    if (line.length + 1 + word.length <= room) {
      line += ` ${word}`;
    } else {
      lines.push(indent + line);
      line = word.length > room ? word.slice(0, room) : word;
    }
  }
  if (line) lines.push(indent + line);
  return lines;
}

export function innerRule(width = INNER_PRINTER_COLUMNS): string {
  return '-'.repeat(width);
}
